import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  withStyles,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import _ from "lodash";

import { CustomTheme } from "../../../../../types/customTheme";
import { getLocalStore } from "../../../../../utils/store/localStore";
import { handleImageUrl } from "../../../../../utils/ChangePixelImgUrl";
import { hexToRgb } from "../../../../../utils/ConvertColorCodeHexToRgb";
import ItemModal from "./ItemModal";
import SwitchCommon from "../../../../../components/switches/SwitchCommon";

const useStyles = makeStyles((theme: any) =>
  createStyles({
    imgBoxStyle: {
      margin: "7px 0 7px 10px",
      borderRadius: "10px",
      height: "120px",
      [theme.breakpoints.down("sm")]: { height: "92px" },
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
    imgBoxNotAvailableStyle: {
      margin: "10px 7px 10px 10px",
      borderRadius: "10px",
      height: "114px",
      width: "114px",
      [theme.breakpoints.down("sm")]: { height: "80px", width: "80px" },
      backgroundColor: theme.palette.background.imgBackground,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    img: {
      objectFit: "cover",
      width: "114px",
      height: "114px",
      [theme.breakpoints.down("sm")]: { width: "88px", height: "84px" },
      borderRadius: "10px",
      border: `1px solid ${theme.palette.background.entity_border}`,
    },
    titleStyle: {
      display: "-webkit-box",
      "-webkit-line-clamp": 2,
      "-webkit-box-orient": "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("sm")]: {
        "-webkit-line-clamp": 1,
      },
    },
    boxStyle: {
      display: "-webkit-box",
      "-webkit-line-clamp": 3,
      "-webkit-box-orient": "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textTransform: "capitalize",
    },
  }),
);



export interface CategoryMenuListProps {
  item: any;
  isColorChange: any;
  data: any;
  handleSelectProduct: any;
  selectedWrapperIdList: any;
  menuProducts: any;
  setSelectedWrapperIdList: any;
  offerItemMap: any;
}

const CategoryMenuList: React.FC<CategoryMenuListProps> = ({
  item,
  isColorChange,
  data,
  handleSelectProduct,
  selectedWrapperIdList,
  menuProducts,
  setSelectedWrapperIdList,
  offerItemMap,
}) => {
  const classes = useStyles();
  const theme: CustomTheme = useTheme();
  const matchesLargeScreen = useMediaQuery("(max-width:960px)");
  const [color, setColor] = useState("");
  const [isOpenItemModal, setIsOpenItemModal] = useState(false);
  const [rotation, setRotation] = useState(matchesLargeScreen ? 2 : 3);
  const [keys, setKeys] = useState<number[]>([]);

  const BlueTooltip = withStyles({
  tooltip: {
    backgroundColor: theme.palette.background.imgBackground, // 💙 blue background
    color: "#fff",              // white text
    fontSize: "0.875rem",
    padding: "8px 12px",
    borderRadius: "8px",
  },
  arrow: {
    color: theme.palette.background.imgBackground, // arrow color matches background
  },
})(Tooltip);

  const isDarkMode = getLocalStore("isDarkMode");

  // Extract keys from selectedWrapperIdList
  useEffect(() => {
    const inner = Object.values(selectedWrapperIdList) || [];
    const extractedKeys = inner.flatMap((obj: any) =>
      Object.keys(obj).map((k) => Number(k)),
    );
    setKeys(extractedKeys);
  }, [selectedWrapperIdList]);

  // Rotation animation effect
  useEffect(() => {
    const startTime = performance.now();
    const initialRotation = matchesLargeScreen ? 2 : 3;
    const duration = data?.wrapper?.length * 100 || 0;

    const rotate = (timestamp: number) => {
      const progress = timestamp - startTime;
      const step = initialRotation - (initialRotation / duration) * progress;
      setRotation(step);

      if (progress < duration) requestAnimationFrame(rotate);
    };

    requestAnimationFrame(rotate);
    return () => setRotation(initialRotation);
  }, [isColorChange]);

  // Color animation effect
  useEffect(() => {
    if (!data?.length) return;
    let startTime: number;
    const duration = data.length * 400;
    const startColor = hexToRgb(
      theme.palette.background.menu_entity_background,
    );
    const endColor = hexToRgb(theme.palette.background.entity_background);

    const animateColor = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;

      const newColor = startColor.map(
        (start, i) => start + ((endColor[i] - start) * progress) / duration,
      );
      setColor(`rgb(${newColor[0]}, ${newColor[1]}, ${newColor[2]})`);

      if (progress < duration) requestAnimationFrame(animateColor);
    };

    requestAnimationFrame(animateColor);
    const timeoutId = setTimeout(() => setColor(""), 2000);
    return () => {
      clearTimeout(timeoutId);
      if (!_.isEmpty(color)) setColor("");
    };
  }, [isColorChange]);

  // Reset color on dark mode change
  useEffect(() => {
    if (!_.isEmpty(color)) setColor("");
  }, [isDarkMode]);

  const handleClick = (event: any) => {
    if (menuProducts[item.id]?.length === 1) {
      handleSelectProduct(event, item, menuProducts[item.id][0]);
    } else {
      setIsOpenItemModal(true);
    }
  };

  // Determine background color
  const getBackgroundColor = () => {
    if (item?.valid === false) return theme.palette.custom.red.main;

    const products = menuProducts[item.id] || [];
    const hasSelected = products.some((p: any) =>
      keys.includes(parseFloat(p.id)),
    );
    return hasSelected
      ? theme.palette.custom.blue.main
      : theme.palette.background.entity_background;
  };
  const isHighlighted =
    item?.valid === false ||
    menuProducts[item.id]?.some((p: any) => keys.includes(parseFloat(p.id)));

  const abc = {
    1944: {
      activeCount: 3,
      deActiveCount: 0,
      idList: [
        "58f5c338-e870-4b78-996b-fd97360c2bb7",
        "5aaa3fd1-c222-4afd-acb9-42db35384ad1",
        "e51a6340-37d6-4f8c-ae6c-3752ad81cb24",
      ],
      offerNameList: [
        "Burger + Fries + Drink £8.99",
        "Buy 2 Get 1 Free – Burgers",
        " Meal Deal for £9.99",
      ],
    },
  };
  return (
    <>
      <div style={{ overflow: "hidden", zIndex: 0 }} onClick={handleClick}>
        <Card
          style={{
            overflowY: "hidden",
            cursor: "pointer",
            borderRadius: "10px",
            border: `1px solid ${theme.palette.background.entity_border}`,
            boxShadow: "none",
            backgroundColor: getBackgroundColor(),
          }}
        >
          <Grid container>
            <Grid item xs={12} style={{ display: "flex" }}>
              {item?.img ? (
                <Box className={classes.imgBoxStyle}>
                  <img
                    src={handleImageUrl(item.img)}
                    alt="Item"
                    className={classes.img}
                  />
                </Box>
              ) : (
                <Box className={classes.imgBoxNotAvailableStyle}>
                  <ImageOutlinedIcon style={{ fontSize: "52px" }} />
                </Box>
              )}

              <Grid
                item
                xs
                container
                direction="column"
                style={{
                  paddingLeft: 8,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    fontWeight: "bold",
                    color: item?.valid === false ? "white" : undefined,
                    paddingTop: 4,
                  }}
                  className={classes.titleStyle}
                >
                  {item?.name}
                </Typography>
                <Typography
                  className={classes.boxStyle}
                  variant="body2"
                  style={{
                    textTransform: "lowercase",
                    color: item?.valid === false ? "white" : undefined,
                  }}
                >
                  {item?.desc}
                </Typography>
                {offerItemMap[menuProducts[item.id][0]?.id]?.activeCount && (
                   <BlueTooltip
                  
                    title={
                      offerItemMap[menuProducts[item.id][0]?.id]?.offerNameList
                        ?.length ? (
                        <div>
                          <Typography
                            style={{ fontWeight: "bold", marginBottom: "4px" }}
                          >
                            Active Offers:
                          </Typography>
                          {offerItemMap[
                            menuProducts[item.id][0]?.id
                          ]?.offerNameList.map(
                            (name: string, index: number) => (
                              <Typography
                                key={index}
                                variant="body2"
                                style={{
                                  whiteSpace: "nowrap",
                                  fontSize: "12px",
                                }}
                              >
                                {`• ${name}`}
                              </Typography>
                            ),
                          )}
                        </div>
                      ) : (
                        "No Active Offers"
                      )
                    }
                    placement="top"
                    arrow
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "end",
                        paddingRight: "12px",
                        paddingBottom: "8px",
                      }}
                    >
                      <Typography
                        style={{
                          paddingRight: "4px",
                          color: isHighlighted
                            ? theme.palette.custom.orange.contrastText
                            : undefined,
                        }}
                      >
                        included in
                      </Typography>
                      <Typography
                        style={{
                          color: isHighlighted
                            ? theme.palette.custom.orange.contrastText
                            : theme.palette.custom.green.main,
                        }}
                      >
                        {`${
                          offerItemMap[menuProducts[item.id][0]?.id].activeCount
                        } active`}
                      </Typography>
                      <Typography
                        style={{
                          paddingLeft: "4px",
                          paddingRight: "4px",
                          color: isHighlighted
                            ? theme.palette.custom.orange.contrastText
                            : undefined,
                        }}
                      >
                        /
                      </Typography>
                      <Typography
                        style={{
                          color: isHighlighted
                            ? theme.palette.custom.orange.contrastText
                            : theme.palette.custom.red.main,
                        }}
                      >
                        {`${
                          offerItemMap[menuProducts[item.id][0]?.id]
                            .deActiveCount
                        } inactive offers`}
                      </Typography>
                    </div>
                  </BlueTooltip>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </div>

      {isOpenItemModal && (
        <ItemModal
          isOpen={isOpenItemModal}
          setIsOpen={setIsOpenItemModal}
          itemNode={menuProducts[item.id]}
          selectedWrapperIdList={selectedWrapperIdList}
          img={item?.img}
          itemId={item.id}
          setSelectedWrapperIdList={setSelectedWrapperIdList}
          keys={keys}
        />
      )}
    </>
  );
};

export default CategoryMenuList;
